/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { createContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useToggle } from 'react-use';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext/ActivePosEntityContextV2';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { SpinnerWrapper } from 'src/core/POS/PosSpinner/PosSpinner.styled';
import { TabItem } from 'src/core/ui/TabPanel/TabPanel.types';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import {
  ActionOutboxEntityType,
  Event,
  Feature,
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingDetailsAutoPricingSectionUpdates,
  ListingDetailsBroadcastSectionUpdates,
  ListingDetailsPricingSectionUpdates,
  ListingDetailsTagsSectionUpdates,
  ListingDetailsTicketSectionUpdates,
} from 'src/WebApiController';

import {
  getListingDataFieldsForTab,
  ListingTab,
} from './components/listingDetailsUtils';
import { useHasListingPricePermissions } from './components/useHasListingPricePermissions';
import { useListingDetailsMethods } from './ListingDetailsModalV2.hooks';
import { ListingDetailsAutoPricingForm } from './ListingForms/AutoPricingForm';
import { useAutoPricingSidePanelContext } from './ListingForms/AutoPricingSidePanel';
import { ListingDetailsBroadcastForm } from './ListingForms/BroadcastForm';
import { ListingDetailsPricingForm } from './ListingForms/PricingForm';
import { ListingDetailsPurchaseForm } from './ListingForms/PurchaseForm';
import { ListingDetailsSalesForm } from './ListingForms/SalesForm';
import { ListingDetailsTagsForm } from './ListingForms/TagsForm';
import { ListingDetailsTicketsForm } from './ListingForms/TicketsForm';

type ModalMethods = {
  broadcast: UseFormReturn<
    ListingDetailsBroadcastSectionUpdates,
    any,
    undefined
  >;
  tickets: UseFormReturn<ListingDetailsTicketSectionUpdates, any, undefined>;
  tags: UseFormReturn<ListingDetailsTagsSectionUpdates, any, undefined>;
  pricing: UseFormReturn<ListingDetailsPricingSectionUpdates, any, undefined>;
  autoPricing: UseFormReturn<
    ListingDetailsAutoPricingSectionUpdates,
    any,
    undefined
  >;
};

type ListingDetailsModalContextType = {
  event: Event | null | undefined;
  listing: ListingDetails | null | undefined;
  tabs: TabItem[];
  isLoading: boolean;
  hasChanges: boolean;
  hasSelectedSections: boolean;
  activeTabIndex: number;
  setLoading: (nextValue: boolean) => void;
  onTabChange: (v: ListingTab) => void;
  setActiveTabIndex: React.Dispatch<React.SetStateAction<number>>;
  onSubmit: () => void;
  onReset: (tabIndex: number) => void;
  methods: ModalMethods;
};

const ListingDetailsModalContext =
  createContext<ListingDetailsModalContextType>({
    event: null,
    listing: null,
    tabs: [],
    isLoading: false,
    hasChanges: false,
    hasSelectedSections: false,
    activeTabIndex: 0,
    setLoading: (_: boolean) => void 0,
    onTabChange: (_: ListingTab) => void 0,
    setActiveTabIndex: (_) => void 0,
    onSubmit: () => void 0,
    onReset: (_: number) => void 0,
    methods: {} as ModalMethods,
  });

export const ListingDetailsModalContextProvider = ({
  children,
}: PropsWithChildren) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const {
    event,
    setActivePosEntity,
    updateActivePosEntityInfo,
    posEntity: listing,
  } = useActivePosEntityContext<ListingDetails>(ActionOutboxEntityType.Listing);

  const hasStandaloneEntityDetailModalsFeature = useUserHasFeature(
    Feature.StandaloneEntityDetailModals
  );
  const hasPersistentTagCreateFeature = useUserHasFeature(
    Feature.PersistentTagCreate
  );

  const { onShowContent, onCloseSidePanel } = useAutoPricingSidePanelContext();

  const hasAutoPricingEmptySelectedSectionsNoCompsFeature = useUserHasFeature(
    Feature.AutoPricingEmptySelectedSectionsNoComps
  );

  const [isLoading, setLoading] = useToggle(false);
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0);

  const onSaveCompleted = useCallback(
    async (success = true) => {
      if (
        activeAccountWebClientConfig?.activeAccountId &&
        listing?.id &&
        success
      ) {
        // Since update can have rippling affect through the whole listing, we need to get the whole thing and
        // update it in both the active entity and the catalog event context
        const updatedListing = await new ListingClient(
          activeAccountWebClientConfig
        ).getListingByListingId(listing.id, undefined, undefined);

        if (updatedListing) {
          updateActivePosEntityInfo({
            event,
            posEntity: updatedListing,
            posEntityId: updatedListing.id,
            posEntityDisplayId: updatedListing.idOnMkp,
          });
        }
      }
      setLoading(false);
    },
    [
      activeAccountWebClientConfig,
      event,
      listing?.id,
      setLoading,
      updateActivePosEntityInfo,
    ]
  );

  const onSaveStart = useCallback(() => {
    setLoading(true);
  }, [setLoading]);

  const {
    updated,
    isSaveEnabled: hasSelectedSections,
    methods,
    onSubmits,
    onResets,
  } = useListingDetailsMethods(listing, onSaveStart, onSaveCompleted);

  const { canSetPrice, canAutoPrice } = useHasListingPricePermissions(listing);

  const tabs: TabItem[] = useMemo(() => {
    if (listing == null) {
      return [];
    }

    const spinner = (
      <SpinnerWrapper>
        <PosSpinner />
      </SpinnerWrapper>
    );

    const listingTabs = [
      {
        value: ListingTab.Broadcast,
        title: <Content id={ContentId.Broadcast} />,
        content: listing?.dataIsLoaded[ListingDetailDataField.Basic] ? (
          <ListingDetailsBroadcastForm listing={listing} />
        ) : (
          spinner
        ),
      },
      {
        value: ListingTab.Tickets,
        title: <Content id={ContentId.Tickets} />,
        content: listing?.dataIsLoaded[ListingDetailDataField.Tickets] ? (
          <ListingDetailsTicketsForm event={event} listing={listing} />
        ) : (
          spinner
        ),
      },
    ];

    if (canSetPrice) {
      listingTabs.push({
        value: ListingTab.Pricing,
        title: <Content id={ContentId.Pricing} />,
        content: listing?.dataIsLoaded[ListingDetailDataField.Pricing] ? (
          <ListingDetailsPricingForm listing={listing} />
        ) : (
          spinner
        ),
      });
    }

    if (canAutoPrice) {
      listingTabs.push({
        value: ListingTab.AutoPricing,
        title: <Content id={ContentId.Autopricing} />,
        // AutoPricing does it's only load logic so it doesn't use the ListingDetailDataField check/load
        content: <ListingDetailsAutoPricingForm listing={listing} />,
      });
    }

    listingTabs.push(
      ...[
        {
          value: ListingTab.Sales,
          title: <Content id={ContentId.Sales} />,
          content: listing?.dataIsLoaded[ListingDetailDataField.Sales] ? (
            <ListingDetailsSalesForm listing={listing} />
          ) : (
            spinner
          ),
        },
        {
          value: ListingTab.Purchase,
          title: <Content id={ContentId.Purchase} />,
          content: listing?.dataIsLoaded[ListingDetailDataField.Purchase] ? (
            <ListingDetailsPurchaseForm listing={listing} />
          ) : (
            spinner
          ),
        },
        {
          value: ListingTab.Tags,
          title: <Content id={ContentId.Tags} />,
          content: listing?.dataIsLoaded[ListingDetailDataField.Tags] ? (
            <ListingDetailsTagsForm listing={listing} />
          ) : (
            spinner
          ),
        },
      ]
    );
    return listingTabs;
  }, [canAutoPrice, canSetPrice, event, listing]);

  const loadDataForTab = useCallback(
    (tab: ListingTab) => {
      if (listing?.id == null) {
        // main listing not even loaded, can't get partial data
        return;
      }

      switch (tab) {
        case ListingTab.Broadcast:
          {
            const dataField = getListingDataFieldsForTab(ListingTab.Broadcast, [
              ListingDetailDataField.Tickets,
            ]);
            if (
              dataField.some(
                (f) => !listing?.dataIsLoaded[f as ListingDetailDataField]
              )
            ) {
              setActivePosEntity(
                listing.id,
                listing.idOnMkp,
                false,
                getListingDataFieldsForTab(ListingTab.Broadcast),
                false,
                true
              );
            }
          }
          break;
        case ListingTab.Tickets:
          {
            const dataField = getListingDataFieldsForTab(ListingTab.Tickets);
            if (
              dataField.some(
                (f) => !listing?.dataIsLoaded[f as ListingDetailDataField]
              )
            ) {
              setActivePosEntity(
                listing.id,
                listing.idOnMkp,
                false,
                getListingDataFieldsForTab(ListingTab.Tickets),
                false,
                true
              );
            }
          }
          break;
        case ListingTab.Pricing:
          {
            const dataField = getListingDataFieldsForTab(ListingTab.Pricing);
            if (
              dataField.some(
                (f) => !listing?.dataIsLoaded[f as ListingDetailDataField]
              )
            ) {
              if (!listing?.mkpListings?.length) {
                setActivePosEntity(
                  listing.id,
                  listing.idOnMkp,
                  false,
                  getListingDataFieldsForTab(ListingTab.Pricing),
                  false,
                  true
                );
              }
            }
          }
          break;
        case ListingTab.AutoPricing:
          {
            const dataField = getListingDataFieldsForTab(
              ListingTab.AutoPricing
            );
            if (
              dataField.some(
                (f) => !listing?.dataIsLoaded[f as ListingDetailDataField]
              )
            ) {
              setActivePosEntity(
                listing.id,
                listing.idOnMkp,
                false,
                getListingDataFieldsForTab(ListingTab.AutoPricing),
                false,
                true
              );
            }
          }
          break;
        case ListingTab.Tags:
          {
            // Need to load purchase to fill origin entity id when creating new persistent tags
            const extraFields = hasPersistentTagCreateFeature
              ? [ListingDetailDataField.Purchase]
              : [];
            const dataField = getListingDataFieldsForTab(
              ListingTab.Tags,
              extraFields
            );
            if (
              dataField.some(
                (f) => !listing?.dataIsLoaded[f as ListingDetailDataField]
              )
            ) {
              setActivePosEntity(
                listing.id,
                listing.idOnMkp,
                false,
                getListingDataFieldsForTab(ListingTab.Tags, extraFields),
                false,
                true
              );
            }
          }
          break;
        case ListingTab.Sales:
          {
            const dataField = getListingDataFieldsForTab(ListingTab.Sales);
            if (
              dataField.some(
                (f) => !listing?.dataIsLoaded[f as ListingDetailDataField]
              )
            ) {
              setActivePosEntity(
                listing.id,
                listing.idOnMkp,
                false,
                getListingDataFieldsForTab(ListingTab.Sales),
                false,
                true
              );
            }
          }
          break;
        case ListingTab.Purchase:
          {
            const dataField = getListingDataFieldsForTab(ListingTab.Purchase);
            if (
              dataField.some(
                (f) => !listing?.dataIsLoaded[f as ListingDetailDataField]
              )
            ) {
              setActivePosEntity(
                listing.id,
                listing.idOnMkp,
                false,
                getListingDataFieldsForTab(ListingTab.Purchase),
                false,
                true
              );
            }
          }
          break;
        default:
          break;
      }
    },
    [
      hasPersistentTagCreateFeature,
      listing?.dataIsLoaded,
      listing?.id,
      listing?.idOnMkp,
      listing?.mkpListings?.length,
      setActivePosEntity,
    ]
  );

  const onReset = useCallback(
    (tabIndex: number) => {
      if (tabIndex >= tabs.length) {
        return;
      }
      switch (tabs[tabIndex].value) {
        case ListingTab.Broadcast:
          onResets.broadcast();
          break;
        case ListingTab.Tickets:
          onResets.tickets();
          break;
        case ListingTab.Pricing:
          onResets.pricing();
          break;
        case ListingTab.AutoPricing:
          onResets.autoPricing();
          break;
        case ListingTab.Tags:
          onResets.tags();
          break;
        default:
          break;
      }
    },
    [onResets, tabs]
  );
  const onTabChange = useCallback(
    (value: ListingTab) => {
      if (value === ListingTab.AutoPricing) {
        onShowContent('comparables');
      } else {
        onCloseSidePanel();
      }

      const tabIndex = tabs.findIndex((t) => t.value === value);
      if (tabIndex !== activeTabIndex) {
        onReset(activeTabIndex);

        loadDataForTab(value);
        setActiveTabIndex(tabIndex ?? 0);
      }
    },
    [
      tabs,
      activeTabIndex,
      onShowContent,
      onCloseSidePanel,
      onReset,
      loadDataForTab,
    ]
  );

  const onSubmit = useCallback(() => {
    if (activeTabIndex >= tabs.length) {
      return;
    }
    switch (tabs[activeTabIndex].value) {
      case ListingTab.Broadcast:
        onSubmits.broadcast();
        break;
      case ListingTab.Tickets:
        onSubmits.tickets();
        break;
      case ListingTab.Pricing:
        onSubmits.pricing();
        break;
      case ListingTab.AutoPricing:
        onSubmits.autoPricing();
        break;
      case ListingTab.Tags:
        onSubmits.tags();
        break;
      default:
        break;
    }
  }, [activeTabIndex, onSubmits, tabs]);

  const hasChanges = useMemo(() => {
    if (activeTabIndex >= tabs.length) {
      return false;
    }
    switch (tabs[activeTabIndex].value) {
      case ListingTab.Broadcast:
        return updated.broadcast;
      case ListingTab.Tickets:
        return updated.tickets;
      case ListingTab.Pricing:
        return updated.pricing;
      case ListingTab.AutoPricing:
        return updated.autoPricing;
      case ListingTab.Tags:
        return updated.tags;
      default:
        return false;
    }
  }, [activeTabIndex, tabs, updated]);

  const hasSelectedSectionsForSave = useMemo(() => {
    // If feature flag is not enabled, always return true (no validation)
    if (!hasAutoPricingEmptySelectedSectionsNoCompsFeature) {
      return true;
    }

    if (activeTabIndex >= tabs.length) {
      return true;
    }
    switch (tabs[activeTabIndex].value) {
      case ListingTab.AutoPricing:
        return hasSelectedSections.autoPricing;
      default:
        return true;
    }
  }, [
    activeTabIndex,
    tabs,
    hasSelectedSections,
    hasAutoPricingEmptySelectedSectionsNoCompsFeature,
  ]);

  return (
    <ListingDetailsModalContext.Provider
      value={{
        event,
        listing,
        tabs,
        isLoading,
        activeTabIndex,
        hasChanges,
        hasSelectedSections: hasSelectedSectionsForSave,
        setLoading,
        setActiveTabIndex,
        onTabChange,
        onSubmit,
        onReset,
        methods,
      }}
    >
      {children}
    </ListingDetailsModalContext.Provider>
  );
};

export const useListingDetailsContext = () =>
  useContext(ListingDetailsModalContext);
